import React, { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { format } from 'date-fns'
import { pt } from 'date-fns/locale'
import {
  getAgendamento,
  updateEstadoAgendamento,
  updateAgendamento,
  deleteAgendamento,
  getProfissionais,
  getSalas,
  verificarConflitoSala,
  getNotificacoesAgendamento,
  remarcarAgendamento,
  getHistoricoAgendamento,
  getPagamentosDoAgendamento,
  getAgendamentos,
  getBloqueiosDoDia,
  getConfiguracoes,
  cancelarFaturaAgendamento,
  descarregarFaturaPdf,
} from '../../lib/supabase'
import { startOfDay, endOfDay } from 'date-fns'
import { valoresAgendamento, estadoParcelas, parcelasPorCobrar, parcelasPorFaturar, fmtEur, labelMetodo } from '../../lib/valores'
import { useTenant } from '../../contexts/TenantContext'
import { useAuth } from '../../contexts/AuthContext'
import { useToast } from '../../contexts/ToastContext'
import { ESTADOS, ORDEM_ESTADOS } from '../../lib/estados'
import Loading from '../../components/Loading'
import { useResponsive } from '../../hooks/useResponsive'

function telLimpo(tel) {
  if (!tel) return ''
  const d = tel.replace(/[\s\-().]/g, '')
  if (d.startsWith('+')) return d.slice(1)
  if (d.startsWith('00')) return d.slice(2)
  if (d.startsWith('351')) return d
  return '351' + d
}

function Linha({ icone, label, children }) {
  if (!children) return null
  return (
    <div style={{ display: 'flex', gap: '12px', padding: '12px 0', borderBottom: '1px solid var(--cor-borda)' }}>
      <span aria-hidden="true" style={{ fontSize: '17px', width: '24px', textAlign: 'center', flexShrink: 0 }}>{icone}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--cor-texto-suave)', marginBottom: '2px' }}>{label}</div>
        <div style={{ fontSize: '15px' }}>{children}</div>
      </div>
    </div>
  )
}

// Motivos de remarcação (lista revista). "Outro" abre campo livre.
const MOTIVOS_REMARCACAO = [
  'A pedido da cliente',
  'Indisponibilidade do profissional',
  'Reagendamento interno',
  'Conflito de horário',
  'Outro',
]

const TIPO_LABEL = { confirmacao: 'Confirmação', lembrete: 'Lembrete' }
const CANAL_ICONE = { sms: '📱', email: '✉️' }
const ESTADO_CFG = {
  enviado:  { cor: '#16A34A', bg: '#F0FDF4', borda: '#BBF7D0', label: 'Enviado' },
  erro:     { cor: '#DC2626', bg: '#FEF2F2', borda: '#FECACA', label: 'Erro' },
  ignorado: { cor: '#6B7280', bg: '#F9FAFB', borda: '#E5E7EB', label: 'Ignorado' },
}

const valInput = { width: '100%', padding: '9px 12px', border: '1px solid var(--cor-borda)', borderRadius: 'var(--raio)', fontSize: '14px', fontFamily: 'var(--font-ui)', background: 'var(--cor-branco)', color: 'var(--cor-texto)' }

// Estilo uniforme dos botões de documento (Talão / Fatura / Vendus) na secção
// Pagamentos — mesmo layout do antigo "Abrir Talão".
const botaoDoc = { background: 'none', border: '1px solid var(--cor-borda)', borderRadius: 'var(--raio)', padding: '4px 10px', fontSize: '12px', color: 'var(--cor-texto)', cursor: 'pointer' }

function ValorBox({ titulo, valor, sub }) {
  return (
    <div style={{ background: 'var(--cor-creme)', borderRadius: 'var(--raio)', padding: '12px' }}>
      <div style={{ fontSize: '11px', color: 'var(--cor-texto-suave)', marginBottom: '4px' }}>{titulo}</div>
      <div style={{ fontSize: '18px', fontWeight: '700' }}>{valor}</div>
      {sub && <div style={{ fontSize: '11px', color: 'var(--cor-texto-suave)', marginTop: '2px' }}>{sub}</div>}
    </div>
  )
}

// Texto de estado de uma parcela (sinal|restante) para o sub-título do ValorBox.
function estadoParcelaTexto(p, { futuro = false } = {}) {
  if (p.pago) {
    const meio = p.origem === 'stripe' ? 'Stripe (online)' : (p.metodo ? labelMetodo(p.metodo) : 'pago')
    return `✓ pago · ${meio}${p.faturado ? ' · faturado' : ''}`
  }
  if (futuro) return 'a definir (após o serviço)'
  return Number(p.valorSugerido) > 0 ? 'por cobrar' : '—'
}

// Agrupa as parcelas faturadas pelo id do documento Vendus (fatura_id). Quando
// sinal e restante saíram na MESMA fatura (mesmo id), fica um só grupo → um só
// botão "Obter fatura". Quando saíram em documentos diferentes (ou só um está
// faturado), há um grupo por documento → "Obter fatura do sinal / do restante".
function faturasEmitidas(est) {
  const parcelas = [
    { tipo: 'sinal', label: 'sinal', p: est.sinal },
    { tipo: 'restante', label: 'restante', p: est.restante },
  ].filter(x => x.p.faturado && x.p.fatura_id)

  const grupos = new Map() // fatura_id → { faturaId, tipos:Set }
  for (const { label, p } of parcelas) {
    const g = grupos.get(p.fatura_id) || { faturaId: p.fatura_id, tipos: new Set() }
    g.tipos.add(label)
    grupos.set(p.fatura_id, g)
  }

  return [...grupos.values()].map(g => {
    // Uma fatura só é "a fatura" (sem sufixo) quando cobre sinal + restante.
    const label = g.tipos.size === 2
      ? 'Obter fatura'
      : (g.tipos.has('sinal') ? 'Obter fatura do sinal' : 'Obter fatura do restante')
    return { faturaId: g.faturaId, label }
  })
}

// Botão de fatura emitida: descarrega o PDF real do documento (via proxy) e
// abre-o numa nova aba.
function BotaoObterFatura({ label, aObter, onClick }) {
  return (
    <button
      onClick={onClick}
      disabled={aObter}
      style={{ ...botaoDoc, display: 'inline-flex', alignItems: 'center', gap: '6px', opacity: aObter ? 0.6 : 1, cursor: aObter ? 'default' : 'pointer' }}
      title="Obter e abrir o PDF da fatura"
    >
      🧾 {aObter ? 'A obter…' : label}
    </button>
  )
}

function SecaoNotificacoes({ notificacoes }) {
  if (!notificacoes || notificacoes.length === 0) return null
  return (
    <div style={{ background: 'var(--cor-branco)', border: '1px solid var(--cor-borda)', borderRadius: 'var(--raio-lg)', boxShadow: 'var(--sombra)', padding: '16px 20px', marginBottom: '16px' }}>
      <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--cor-texto-suave)', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        Notificações enviadas
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {notificacoes.map(n => {
          const cfg = ESTADO_CFG[n.estado] || ESTADO_CFG.ignorado
          return (
            <div key={n.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '10px 12px', borderRadius: 'var(--raio)', background: cfg.bg, border: `1px solid ${cfg.borda}` }}>
              <span aria-hidden="true" style={{ fontSize: '16px', flexShrink: 0, marginTop: '1px' }}>{CANAL_ICONE[n.canal] || '🔔'}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--cor-principal)' }}>
                    {TIPO_LABEL[n.tipo] || n.tipo}
                  </span>
                  <span style={{ fontSize: '12px', color: 'var(--cor-texto-suave)', textTransform: 'capitalize' }}>
                    {n.canal}
                  </span>
                  <span style={{ fontSize: '12px', fontWeight: '600', color: cfg.cor, marginLeft: 'auto' }}>
                    {cfg.label}
                  </span>
                </div>
                {n.detalhe && (
                  <div style={{ fontSize: '12px', color: 'var(--cor-texto-suave)', marginTop: '3px', wordBreak: 'break-word' }}>
                    {n.detalhe}
                  </div>
                )}
                <div style={{ fontSize: '11px', color: 'var(--cor-texto-suave)', marginTop: '4px' }}>
                  {format(new Date(n.enviado_em), "d MMM yyyy 'às' HH:mm", { locale: pt })}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default function DetalheAgendamento() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { tenant } = useTenant()
  const { perfil, isManager, podeFazer } = useAuth()
  const toast = useToast()
  const { isDesktop } = useResponsive()
  const [ag, setAg] = useState(null)
  const [carregando, setCarregando] = useState(true)
  const [erroCarregar, setErroCarregar] = useState(null)
  const [guardando, setGuardando] = useState(false)

  // Edição de profissional/sala
  const [modoEdicao, setModoEdicao] = useState(false)
  const [profissionais, setProfissionais] = useState([])
  const [salas, setSalas] = useState([])
  const [editProfId, setEditProfId] = useState('')
  const [editSalaId, setEditSalaId] = useState('')
  const [confirmarEliminar, setConfirmarEliminar] = useState(false)
  const [eliminando, setEliminando] = useState(false)
  const [salaConflito, setSalaConflito] = useState(false)
  const [verificandoSala, setVerificandoSala] = useState(false)
  const [confirmarConflito, setConfirmarConflito] = useState(false)
  const [notificacoes, setNotificacoes] = useState([])

  // Observações internas
  const [editObs, setEditObs] = useState(false)
  const [obsTexto, setObsTexto] = useState('')
  const [guardandoObs, setGuardandoObs] = useState(false)

  // Remarcação
  const [modoRemarcar, setModoRemarcar] = useState(false)
  const [remProfId, setRemProfId] = useState('')
  const [remData, setRemData] = useState('')
  const [remHora, setRemHora] = useState('')
  const [remMotivo, setRemMotivo] = useState(MOTIVOS_REMARCACAO[0])
  const [remMotivoOutro, setRemMotivoOutro] = useState('')
  const [remNotificar, setRemNotificar] = useState(true)
  const [remConflitos, setRemConflitos] = useState([])
  const [remarcando, setRemarcando] = useState(false)
  const [historico, setHistorico] = useState([])

  // Parcelas de pagamento (sinal/restante) — fonte de verdade dos valores.
  const [pagamentos, setPagamentos] = useState([])
  const [vendusAtivo, setVendusAtivo] = useState(false)
  const [obtendoPdf, setObtendoPdf] = useState(null) // fatura_id em obtenção
  const [confirmarCancelarFatura, setConfirmarCancelarFatura] = useState(false)
  const [cancelandoFatura, setCancelandoFatura] = useState(false)
  const [reabrirPagamentos, setReabrirPagamentos] = useState(false)

  const perfilNome = perfil?.nome || perfil?.email || 'backoffice'

  useEffect(() => {
    if (!tenant || !id) return
    setCarregando(true)
    getAgendamento(tenant.id, id)
      .then(dados => {
        setAg(dados)
        setEditProfId(dados?.profissional_id || '')
        setEditSalaId(dados?.sala_id || '')
      })
      .catch(e => setErroCarregar(e.message))
      .finally(() => setCarregando(false))
    getNotificacoesAgendamento(tenant.id, id)
      .then(setNotificacoes)
      .catch(() => {})
    getHistoricoAgendamento(tenant.id, id)
      .then(setHistorico)
      .catch(() => {})
    getPagamentosDoAgendamento(tenant.id, id)
      .then(setPagamentos)
      .catch(() => {})
    getConfiguracoes(tenant.id)
      .then(c => setVendusAtivo(c?.vendus_ativo === 'true'))
      .catch(() => {})
  }, [tenant, id])


  // Abre o PDF REAL da fatura (o documento, não a página de verificação). Os
  // bytes vêm por proxy da Edge Function (a API key nunca é exposta) e abrem-se
  // numa nova aba via objectURL.
  async function obterFatura(faturaId) {
    if (!faturaId) return
    setObtendoPdf(faturaId)
    try {
      const { url } = await descarregarFaturaPdf(tenant.id, { faturaId })
      // Não revogamos já: a aba precisa do objectURL para o mostrar.
      window.open(url, '_blank', 'noopener')
    } catch (e) {
      toast.erro(e.message)
    } finally {
      setObtendoPdf(null)
    }
  }

  // Cancela a fatura desta marcação (FASE 1 — só do nosso lado). Recarrega as
  // parcelas e a marcação (para refletir as observações atualizadas). Avisa que
  // o documento ainda precisa de ser anulado no Vendus manualmente.
  async function cancelarFatura() {
    setCancelandoFatura(true)
    try {
      const r = await cancelarFaturaAgendamento(tenant.id, id, perfilNome, reabrirPagamentos)
      const [pags, novoAg] = await Promise.all([
        getPagamentosDoAgendamento(tenant.id, id),
        getAgendamento(tenant.id, id),
      ])
      setPagamentos(pags)
      if (novoAg) setAg(novoAg)
      setConfirmarCancelarFatura(false)
      setReabrirPagamentos(false)
      const n = r?.n_marcacoes || 1
      const nReab = r?.pagamentos_reabertos || 0
      const nStripe = r?.stripe_mantidos || 0
      toast.sucesso(
        `Fatura cancelada no sistema${n > 1 ? ` (${n} marcações do documento)` : ''}. ` +
        (nReab > 0 ? `${nReab} pagamento${nReab === 1 ? '' : 's'} reaberto${nReab === 1 ? '' : 's'} — volta a aparecer como por cobrar. ` : '') +
        (nStripe > 0 ? `${nStripe} pagamento${nStripe === 1 ? '' : 's'} por Stripe manteve-se pago (esses nunca reabrem). ` : '') +
        `Anule também o documento ${r?.fatura_id || ''} no Vendus.`
      )
    } catch (e) {
      toast.erro(e.message)
    } finally {
      setCancelandoFatura(false)
    }
  }

  function abrirEdicao() {
    if (profissionais.length === 0) {
      getProfissionais(tenant.id).then(setProfissionais).catch(() => {})
    }
    if (salas.length === 0) {
      getSalas(tenant.id).then(setSalas).catch(() => {})
    }
    setSalaConflito(false)
    setModoEdicao(true)
  }

  async function aoMudarSala(novoSalaId) {
    setEditSalaId(novoSalaId)
    setSalaConflito(false)
    if (!novoSalaId || !ag) return
    setVerificandoSala(true)
    try {
      const conflito = await verificarConflitoSala(tenant.id, novoSalaId, ag.data_hora_inicio, ag.data_hora_fim, ag.id)
      setSalaConflito(conflito)
    } catch {
      // silencioso — o guardar vai apanhar o erro
    } finally {
      setVerificandoSala(false)
    }
  }

  async function guardarEdicao(forcar = false) {
    if (!ag) return
    setGuardando(true)
    try {
      await updateAgendamento(
        tenant.id,
        ag.id,
        { profissional_id: editProfId, sala_id: editSalaId || null },
        perfilNome,
        forcar,
      )
      const atualizado = await getAgendamento(tenant.id, ag.id)
      setAg(atualizado)
      setModoEdicao(false)
      setSalaConflito(false)
      setConfirmarConflito(false)
      toast.sucesso('Marcação atualizada.')
    } catch (e) {
      toast.erro(e.message)
    } finally {
      setGuardando(false)
    }
  }

  function abrirEdicaoObs() {
    setObsTexto(ag?.observacoes || '')
    setEditObs(true)
  }

  async function guardarObs() {
    setGuardandoObs(true)
    try {
      const valor = obsTexto.trim() || null
      await updateAgendamento(tenant.id, id, { observacoes: valor }, perfilNome)
      setAg(prev => ({ ...prev, observacoes: valor }))
      setEditObs(false)
      toast.sucesso('Observações guardadas.')
    } catch (e) {
      toast.erro(e.message)
    } finally {
      setGuardandoObs(false)
    }
  }

  // ─── Remarcação ───────────────────────────────────────────
  function abrirRemarcar() {
    if (profissionais.length === 0) {
      getProfissionais(tenant.id).then(setProfissionais).catch(() => {})
    }
    const inicio = new Date(ag.data_hora_inicio)
    setRemProfId(ag.profissional_id || '')
    setRemData(format(inicio, 'yyyy-MM-dd'))
    setRemHora(format(inicio, 'HH:mm'))
    setRemMotivo(MOTIVOS_REMARCACAO[0])
    setRemMotivoOutro('')
    setRemNotificar(true)
    setRemConflitos([])
    setModoRemarcar(true)
  }

  // Duração (min) da marcação atual — mantida na remarcação (é a mesma marcação).
  const duracaoMin = ag ? Math.round((new Date(ag.data_hora_fim) - new Date(ag.data_hora_inicio)) / 60000) : 0

  const remInicio = (remData && /^\d{2}:\d{2}$/.test(remHora))
    ? new Date(`${remData}T${remHora}:00`)
    : null
  const remFim = remInicio && duracaoMin
    ? new Date(remInicio.getTime() + duracaoMin * 60000)
    : null

  // Aviso não-bloqueante de sobreposição no novo horário (mesma lógica da Nova
  // Marcação), ignorando a própria marcação.
  const remInicioISO = remInicio ? remInicio.toISOString() : null
  const remFimISO = remFim ? remFim.toISOString() : null
  useEffect(() => {
    if (!modoRemarcar || !tenant || !remProfId || !remInicioISO || !remFimISO) {
      setRemConflitos([])
      return
    }
    let ativo = true
    const inicio = new Date(remInicioISO)
    const fim = new Date(remFimISO)
    const diaInicio = startOfDay(inicio).toISOString()
    const diaFim = endOfDay(inicio).toISOString()
    Promise.all([
      getAgendamentos(tenant.id, diaInicio, diaFim),
      getBloqueiosDoDia(tenant.id, diaInicio, diaFim),
    ])
      .then(([ags, bloqs]) => {
        if (!ativo) return
        const sobrepoe = (ini, f) => new Date(ini) < fim && new Date(f) > inicio
        const lista = []
        ags
          .filter(a => a.id !== ag.id && a.profissional_id === remProfId)
          .filter(a => sobrepoe(a.data_hora_inicio, a.data_hora_fim))
          .forEach(a => lista.push({
            tipo: 'marcação',
            label: `${format(new Date(a.data_hora_inicio), 'HH:mm')}–${format(new Date(a.data_hora_fim), 'HH:mm')} · ${a.cliente_nome || 'Sem nome'}`,
          }))
        bloqs
          .filter(b => b.profissional_id === null || b.profissional_id === remProfId)
          .filter(b => sobrepoe(b.data_hora_inicio, b.data_hora_fim))
          .forEach(b => lista.push({
            tipo: b.profissional_id === null ? 'bloqueio do espaço' : 'bloqueio',
            label: `${format(new Date(b.data_hora_inicio), 'HH:mm')}–${format(new Date(b.data_hora_fim), 'HH:mm')}${b.motivo ? ` · ${b.motivo}` : ''}`,
          }))
        setRemConflitos(lista)
      })
      .catch(() => { if (ativo) setRemConflitos([]) })
    return () => { ativo = false }
  }, [modoRemarcar, tenant, remProfId, remInicioISO, remFimISO, ag])

  async function remarcar() {
    if (!remInicio || !remFim) return
    const motivo = remMotivo === 'Outro' ? remMotivoOutro.trim() : remMotivo
    setRemarcando(true)
    try {
      const atualizado = await remarcarAgendamento(tenant.id, id, {
        inicio: remInicio.toISOString(),
        fim: remFim.toISOString(),
        profissionalId: remProfId,
        motivo,
        notificar: remNotificar,
      }, perfilNome)
      // Recarrega para trazer os joins (profissional/serviço) atualizados.
      const [novoAg, novoHist] = await Promise.all([
        getAgendamento(tenant.id, id),
        getHistoricoAgendamento(tenant.id, id),
      ])
      setAg(novoAg || atualizado)
      setHistorico(novoHist)
      setModoRemarcar(false)
      toast.sucesso(remNotificar
        ? 'Marcação remarcada. (Notificação registada — o envio automático ainda não está ativo.)'
        : 'Marcação remarcada.')
    } catch (e) {
      toast.erro(e.message)
    } finally {
      setRemarcando(false)
    }
  }

  async function mudarEstado(novo) {
    if (!ag || novo === ag.estado) return
    setGuardando(true)
    try {
      await updateEstadoAgendamento(tenant.id, ag.id, novo, perfilNome)
      setAg(prev => ({ ...prev, estado: novo, atualizado_por: perfilNome, atualizado_em: new Date().toISOString() }))
      toast.sucesso('Estado atualizado.')
    } catch (e) {
      toast.erro(e.message)
    } finally {
      setGuardando(false)
    }
  }

  async function eliminar() {
    setEliminando(true)
    try {
      await deleteAgendamento(tenant.id, id)
      toast.sucesso('Marcação eliminada.')
      // Reencaminha sempre para a Agenda do Dia (no dia da marcação eliminada).
      // Não usamos navigate(-1) porque, ao abrir por link directo, o histórico
      // pode não ter a agenda — ou até voltar ao próprio agendamento já apagado.
      const dia = format(new Date(ag.data_hora_inicio), 'yyyy-MM-dd')
      navigate(`/admin?data=${dia}`, { replace: true })
    } catch (e) {
      toast.erro(e.message)
      setEliminando(false)
      setConfirmarEliminar(false)
    }
  }

  if (carregando) return <div style={{ padding: '48px 24px' }}><Loading /></div>

  if (erroCarregar || !ag) {
    return (
      <div style={{ maxWidth: '640px', margin: '0 auto', padding: '32px 24px' }}>
        <button onClick={() => navigate(-1)} className="btn-secundario" style={{ marginBottom: '24px' }}>← Voltar</button>
        <div style={{ textAlign: 'center', padding: '48px 24px', color: 'var(--cor-texto-suave)', background: 'var(--cor-branco)', border: '1px solid var(--cor-borda)', borderRadius: 'var(--raio-lg)' }}>
          <p style={{ fontSize: '40px', marginBottom: '12px' }}>🔍</p>
          {erroCarregar || 'Marcação não encontrada.'}
        </div>
      </div>
    )
  }

  const inicio = new Date(ag.data_hora_inicio)
  const fim = new Date(ag.data_hora_fim)
  const estadoCfg = ESTADOS[ag.estado] || { label: ag.estado, cor: '#6B7280' }
  const nome = ag.cliente_nome || ag.clientes?.nome
  const telefone = ag.cliente_telefone || ag.clientes?.telefone
  const email = ag.cliente_email || ag.clientes?.email
  const corProf = ag.profissionais?.cor_agenda || 'var(--cor-borda)'

  return (
    <div style={{ maxWidth: isDesktop ? '1080px' : '640px', margin: '0 auto', padding: '24px 20px 64px' }}>
      {/* Cabeçalho — hora + estado */}
      <div style={{
        position: 'relative', overflow: 'hidden',
        background: 'var(--cor-branco)', border: '1px solid var(--cor-borda)',
        borderRadius: 'var(--raio-lg)', boxShadow: 'var(--sombra)',
        padding: '24px 24px 22px 28px', marginBottom: '16px',
      }}>
        <span style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '6px', background: estadoCfg.cor }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', minWidth: 0 }}>
            <button
              onClick={() => navigate(-1)}
              aria-label="Voltar"
              title="Voltar"
              style={{
                flexShrink: 0, marginTop: '2px',
                width: '34px', height: '34px', borderRadius: 'var(--raio)',
                border: '1px solid var(--cor-borda)', background: 'var(--cor-creme)',
                color: 'var(--cor-principal)', fontSize: '18px', lineHeight: 1,
                cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              ←
            </button>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: '13px', color: 'var(--cor-texto-suave)', textTransform: 'capitalize' }}>
              {format(inicio, "EEEE, d 'de' MMMM", { locale: pt })}
            </div>
            <div style={{ fontSize: '40px', fontWeight: '700', color: 'var(--cor-principal)', lineHeight: 1.1, fontFamily: 'var(--font-ui)' }}>
              {format(inicio, 'HH:mm')}<span style={{ fontSize: '20px', color: 'var(--cor-texto-suave)', fontWeight: '400' }}> – {format(fim, 'HH:mm')}</span>
            </div>
            <div style={{ fontSize: '13px', color: 'var(--cor-texto-suave)', marginTop: '2px' }}>
              {ag.servicos?.duracao_min} min
            </div>
          </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}>
            <span style={{
              display: 'inline-block', padding: '5px 14px', borderRadius: '999px',
              fontSize: '13px', fontWeight: '600',
              background: estadoCfg.cor + '18', color: estadoCfg.cor,
            }}>
              {estadoCfg.label}
            </span>
            <span style={{
              display: 'inline-block', padding: '3px 10px', borderRadius: '999px',
              fontSize: '12px', fontWeight: '500',
              background: ag.origem === 'publico' ? '#F0FDF4' : '#EFF6FF',
              color: ag.origem === 'publico' ? '#15803D' : '#1D4ED8',
              border: `1px solid ${ag.origem === 'publico' ? '#BBF7D0' : '#BFDBFE'}`,
            }}>
              {ag.origem === 'publico' ? 'Site público' : 'Backoffice'}
            </span>
          </div>
        </div>

        <h1 style={{ fontFamily: 'var(--font-base)', fontSize: '28px', fontWeight: '500', color: 'var(--cor-principal)', marginTop: '16px' }}>
          {ag.servicos?.nome || 'Serviço'}
        </h1>
        {ag.servicos?.categoria && (
          <div style={{ fontSize: '12px', color: 'var(--cor-ambar)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
            {ag.servicos.categoria}
          </div>
        )}
      </div>

      <div style={isDesktop ? { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 360px', gap: '20px', alignItems: 'start' } : undefined}>
      <div>
      {/* Detalhes */}
      <div style={{ background: 'var(--cor-branco)', border: '1px solid var(--cor-borda)', borderRadius: 'var(--raio-lg)', boxShadow: 'var(--sombra)', padding: '8px 20px 16px', marginBottom: '16px' }}>
        <Linha icone="👤" label="Cliente">
          {ag.clientes?.id && isManager ? (
            <Link to={`/admin/clientes/${ag.clientes.id}`} style={{ color: 'var(--cor-principal)', fontWeight: '600', textDecoration: 'none' }}>
              {nome || 'Sem nome'}
            </Link>
          ) : (
            <span style={{ fontWeight: '600', fontStyle: nome ? 'normal' : 'italic', color: nome ? 'inherit' : 'var(--cor-texto-suave)' }}>
              {nome || 'Sem nome'}
            </span>
          )}
        </Linha>
        <Linha icone="📞" label="Telefone">
          {telefone && <a href={`tel:${telefone}`} style={{ color: 'inherit', textDecoration: 'none' }}>{telefone}</a>}
        </Linha>
        <Linha icone="✉️" label="Email">
          {email && <a href={`mailto:${email}`} style={{ color: 'inherit', textDecoration: 'none', wordBreak: 'break-all' }}>{email}</a>}
        </Linha>

        {/* Profissional e Sala — com botão de edição */}
        {modoEdicao ? (
          <div style={{ padding: '14px 0', borderBottom: '1px solid var(--cor-borda)' }}>
            <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--cor-texto-suave)', marginBottom: '10px' }}>
              Editar profissional e sala
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <div>
                <label style={{ fontSize: '13px', color: 'var(--cor-texto-suave)', display: 'block', marginBottom: '4px' }}>Profissional</label>
                <select
                  value={editProfId}
                  onChange={e => setEditProfId(e.target.value)}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: 'var(--raio)', border: '1px solid var(--cor-borda)', fontSize: '14px', background: 'var(--cor-branco)' }}
                >
                  {profissionais.map(p => (
                    <option key={p.id} value={p.id}>{p.nome}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={{ fontSize: '13px', color: 'var(--cor-texto-suave)', display: 'block', marginBottom: '4px' }}>Sala</label>
                <select
                  value={editSalaId}
                  onChange={e => aoMudarSala(e.target.value)}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: 'var(--raio)', border: `1px solid ${salaConflito ? '#FCA5A5' : 'var(--cor-borda)'}`, fontSize: '14px', background: 'var(--cor-branco)' }}
                >
                  <option value="">— Sem sala —</option>
                  {salas.map(s => (
                    <option key={s.id} value={s.id}>{s.nome}</option>
                  ))}
                </select>
                {verificandoSala && (
                  <div style={{ fontSize: '12px', color: 'var(--cor-texto-suave)', marginTop: '5px' }}>A verificar disponibilidade…</div>
                )}
                {salaConflito && !verificandoSala && (
                  <div style={{ fontSize: '12px', color: '#DC2626', marginTop: '5px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                    ⚠️ Esta sala já está ocupada neste horário.
                  </div>
                )}
              </div>
              {confirmarConflito ? (
                <div style={{ background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: 'var(--raio)', padding: '12px 14px', marginTop: '4px' }}>
                  <p style={{ fontSize: '13px', color: '#DC2626', fontWeight: '500', marginBottom: '10px' }}>
                    ⚠️ A sala já está ocupada neste horário. Confirmas mesmo assim?
                  </p>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button onClick={() => guardarEdicao(true)} disabled={guardando} style={{ flex: 1, background: '#DC2626', border: 'none', borderRadius: 'var(--raio)', padding: '8px 12px', fontSize: '13px', color: 'white', cursor: guardando ? 'default' : 'pointer', opacity: guardando ? 0.6 : 1 }}>
                      {guardando ? 'A guardar…' : 'Guardar mesmo assim'}
                    </button>
                    <button onClick={() => setConfirmarConflito(false)} disabled={guardando} className="btn-secundario" style={{ flex: 1 }}>
                      Cancelar
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                  <button
                    onClick={() => salaConflito ? setConfirmarConflito(true) : guardarEdicao()}
                    disabled={guardando || verificandoSala}
                    className="btn-principal"
                    style={{ flex: 1 }}
                  >
                    {guardando ? 'A guardar…' : 'Guardar'}
                  </button>
                  <button onClick={() => { setModoEdicao(false); setSalaConflito(false); setConfirmarConflito(false) }} disabled={guardando} className="btn-secundario" style={{ flex: 1 }}>
                    Cancelar
                  </button>
                </div>
              )}
            </div>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', gap: '12px', padding: '12px 0', borderBottom: '1px solid var(--cor-borda)', alignItems: 'center' }}>
              <span aria-hidden="true" style={{ fontSize: '17px', width: '24px', textAlign: 'center', flexShrink: 0 }}>💆</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--cor-texto-suave)', marginBottom: '2px' }}>Profissional</div>
                <div style={{ fontSize: '15px', display: 'inline-flex', alignItems: 'center', gap: '7px' }}>
                  <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: corProf, display: 'inline-block' }} />
                  {ag.profissionais?.nome || '—'}
                </div>
              </div>
              <button
                onClick={abrirEdicao}
                style={{ fontSize: '12px', padding: '4px 10px', borderRadius: 'var(--raio)', border: '1px solid var(--cor-borda)', background: 'transparent', cursor: 'pointer', color: 'var(--cor-texto-suave)', whiteSpace: 'nowrap' }}
              >
                Editar
              </button>
            </div>
            <div style={{ display: 'flex', gap: '12px', padding: '12px 0', borderBottom: '1px solid var(--cor-borda)' }}>
              <span aria-hidden="true" style={{ fontSize: '17px', width: '24px', textAlign: 'center', flexShrink: 0 }}>🚪</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--cor-texto-suave)', marginBottom: '2px' }}>Sala</div>
                <div style={{ fontSize: '15px', color: ag.salas?.nome ? 'inherit' : 'var(--cor-texto-suave)' }}>
                  {ag.salas?.nome || 'Sem sala atribuída'}
                </div>
              </div>
            </div>
          </>
        )}

        <Linha icone="📝" label="Notas">
          {ag.notas ? <span style={{ fontStyle: 'italic' }}>"{ag.notas}"</span> : null}
        </Linha>
      </div>

      {/* Contactos rápidos */}
      {telefone && (
        <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' }}>
          <a href={`tel:${telefone}`} className="btn-secundario" style={{ textDecoration: 'none', flex: 1, textAlign: 'center', minWidth: '120px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
            📞 Ligar
          </a>
          <a href={`https://wa.me/${telLimpo(telefone)}`} target="_blank" rel="noreferrer" className="btn-secundario" style={{ textDecoration: 'none', flex: 1, textAlign: 'center', minWidth: '120px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
            💬 WhatsApp
          </a>
        </div>
      )}

      {/* Observações internas */}
      <div style={{ background: 'var(--cor-branco)', border: '1px solid var(--cor-borda)', borderRadius: 'var(--raio-lg)', boxShadow: 'var(--sombra)', padding: '18px 20px', marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
          <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--cor-texto-suave)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Observações
          </div>
          {!editObs && (
            <button onClick={abrirEdicaoObs} style={{ background: 'none', border: '1px solid var(--cor-borda)', borderRadius: 'var(--raio)', padding: '5px 12px', fontSize: '12px', cursor: 'pointer', color: 'var(--cor-texto-suave)' }}>
              {ag.observacoes ? 'Editar' : 'Adicionar'}
            </button>
          )}
        </div>

        {!editObs ? (
          ag.observacoes ? (
            <p style={{ fontSize: '14px', whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>{ag.observacoes}</p>
          ) : (
            <p style={{ fontSize: '14px', color: 'var(--cor-texto-suave)', fontStyle: 'italic', margin: 0 }}>Sem observações.</p>
          )
        ) : (
          <div>
            <textarea
              value={obsTexto}
              onChange={e => setObsTexto(e.target.value)}
              maxLength={1000}
              placeholder="Notas internas sobre este agendamento…"
              style={{ ...valInput, minHeight: '90px', resize: 'vertical' }}
            />
            <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
              <button onClick={guardarObs} disabled={guardandoObs} className="btn-primario" style={{ padding: '8px 16px', fontSize: '13px', opacity: guardandoObs ? 0.6 : 1 }}>
                {guardandoObs ? 'A guardar…' : 'Guardar'}
              </button>
              <button onClick={() => setEditObs(false)} disabled={guardandoObs} className="btn-secundario" style={{ padding: '8px 16px', fontSize: '13px' }}>
                Cancelar
              </button>
            </div>
          </div>
        )}
      </div>
      </div>

      <div>
      {/* Remarcação */}
      {podeFazer('alterar_estado') && (
      <div style={{ background: 'var(--cor-branco)', border: '1px solid var(--cor-borda)', borderRadius: 'var(--raio-lg)', boxShadow: 'var(--sombra)', padding: '18px 20px', marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: modoRemarcar ? '14px' : '0' }}>
          <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--cor-texto-suave)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Remarcar
          </div>
          {!modoRemarcar && (
            <button onClick={abrirRemarcar} style={{ background: 'none', border: '1px solid var(--cor-borda)', borderRadius: 'var(--raio)', padding: '6px 14px', fontSize: '13px', cursor: 'pointer', color: 'var(--cor-texto)' }}>
              🔁 Remarcar
            </button>
          )}
        </div>

        {modoRemarcar && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div>
              <label style={{ fontSize: '13px', color: 'var(--cor-texto-suave)', display: 'block', marginBottom: '4px' }}>Profissional</label>
              <select value={remProfId} onChange={e => setRemProfId(e.target.value)} style={valInput}>
                {profissionais.map(p => <option key={p.id} value={p.id}>{p.nome}</option>)}
              </select>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <label style={{ fontSize: '13px', color: 'var(--cor-texto-suave)', display: 'block', marginBottom: '4px' }}>Data</label>
                <input type="date" value={remData} onChange={e => setRemData(e.target.value)} style={valInput} />
              </div>
              <div>
                <label style={{ fontSize: '13px', color: 'var(--cor-texto-suave)', display: 'block', marginBottom: '4px' }}>Hora</label>
                <input type="time" step={300} value={remHora} onChange={e => setRemHora(e.target.value)} style={valInput} />
              </div>
            </div>
            {remInicio && remFim && (
              <div style={{ fontSize: '12px', color: 'var(--cor-texto-suave)' }}>
                Das <strong>{format(remInicio, 'HH:mm')}</strong> às <strong>{format(remFim, 'HH:mm')}</strong> ({duracaoMin} min)
              </div>
            )}
            <div>
              <label style={{ fontSize: '13px', color: 'var(--cor-texto-suave)', display: 'block', marginBottom: '4px' }}>Motivo</label>
              <select value={remMotivo} onChange={e => setRemMotivo(e.target.value)} style={valInput}>
                {MOTIVOS_REMARCACAO.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
              {remMotivo === 'Outro' && (
                <input
                  type="text" value={remMotivoOutro} onChange={e => setRemMotivoOutro(e.target.value)}
                  maxLength={200} placeholder="Descreva o motivo…"
                  style={{ ...valInput, marginTop: '8px' }}
                />
              )}
            </div>

            {/* Aviso não-bloqueante de sobreposição */}
            {remConflitos.length > 0 && (
              <div style={{ background: '#FEF3C7', border: '1px solid #F59E0B', borderRadius: 'var(--raio)', padding: '10px 12px' }}>
                <p style={{ fontSize: '13px', fontWeight: '600', color: '#92400E', margin: 0 }}>
                  ⚠️ Sobreposição com {remConflitos.length === 1 ? '1 item' : `${remConflitos.length} itens`} neste horário
                </p>
                <ul style={{ margin: '6px 0 0', paddingLeft: '18px' }}>
                  {remConflitos.map((c, i) => (
                    <li key={i} style={{ fontSize: '12px', color: '#92400E', lineHeight: 1.5 }}>
                      <span style={{ textTransform: 'capitalize' }}>{c.tipo}</span>: {c.label}
                    </li>
                  ))}
                </ul>
                <p style={{ fontSize: '11px', color: '#92400E', margin: '6px 0 0', opacity: 0.85 }}>Podes remarcar à mesma.</p>
              </div>
            )}

            <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px' }}>
              <input type="checkbox" checked={remNotificar} onChange={e => setRemNotificar(e.target.checked)} style={{ width: '16px', height: '16px', accentColor: 'var(--cor-principal)' }} />
              Notificar a cliente
            </label>
            <p style={{ fontSize: '11px', color: 'var(--cor-texto-suave)', margin: '-6px 0 0' }}>
              O envio automático de email/SMS de remarcação ainda não está ativo — por agora fica só registado no histórico.
            </p>

            <div style={{ display: 'flex', gap: '10px', marginTop: '4px' }}>
              <button
                onClick={remarcar}
                disabled={remarcando || !remInicio || !remFim || (remMotivo === 'Outro' && !remMotivoOutro.trim())}
                className="btn-primario"
                style={{ flex: 1, opacity: (remarcando || !remInicio || !remFim || (remMotivo === 'Outro' && !remMotivoOutro.trim())) ? 0.6 : 1 }}
              >
                {remarcando ? 'A remarcar…' : 'Confirmar remarcação'}
              </button>
              <button onClick={() => setModoRemarcar(false)} disabled={remarcando} className="btn-secundario" style={{ flex: 1 }}>
                Cancelar
              </button>
            </div>
          </div>
        )}

        {/* Histórico de remarcações */}
        {historico.length > 0 && (
          <div style={{ marginTop: modoRemarcar ? '16px' : '12px', paddingTop: '12px', borderTop: '1px solid var(--cor-borda)' }}>
            <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--cor-texto-suave)', marginBottom: '8px' }}>
              Histórico de remarcações
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {historico.map(h => (
                <div key={h.id} style={{ fontSize: '13px', padding: '8px 10px', background: 'var(--cor-creme)', borderRadius: 'var(--raio)' }}>
                  <div>
                    <span style={{ color: 'var(--cor-texto-suave)' }}>De </span>
                    <strong>{format(new Date(h.de_inicio), "d MMM 'às' HH:mm", { locale: pt })}</strong>
                    {h.de_prof?.nome && <span style={{ color: 'var(--cor-texto-suave)' }}> ({h.de_prof.nome})</span>}
                    <span style={{ color: 'var(--cor-texto-suave)' }}> → </span>
                    <strong>{format(new Date(h.para_inicio), "d MMM 'às' HH:mm", { locale: pt })}</strong>
                    {h.para_prof?.nome && <span style={{ color: 'var(--cor-texto-suave)' }}> ({h.para_prof.nome})</span>}
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--cor-texto-suave)', marginTop: '3px' }}>
                    {h.motivo && <>{h.motivo} · </>}
                    {h.notificar_cliente ? 'cliente notificada · ' : ''}
                    {h.feito_por && <>por {h.feito_por} · </>}
                    {format(new Date(h.criado_em), "d MMM yyyy 'às' HH:mm", { locale: pt })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      )}
      {/* Valores (preço / sinal / restante) — só leitura; cobrar-se faz na
          página Cobrar (uma só forma de cobrar). */}
      {podeFazer('ver_valores') && (() => {
        const v = valoresAgendamento(ag, pagamentos)
        const est = estadoParcelas(ag, pagamentos)
        // Mesma regra da página Cobrar (uma só fonte de verdade): há algo a
        // cobrar sempre que uma das parcelas não esteja paga.
        const podeCobrar = !!ag.cliente_id &&
          parcelasPorCobrar({ sinal: est.sinal, restante: est.restante }).length > 0
        return (
          <div style={{ background: 'var(--cor-branco)', border: '1px solid var(--cor-borda)', borderRadius: 'var(--raio-lg)', boxShadow: 'var(--sombra)', padding: '18px 20px', marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px', gap: '12px', flexWrap: 'wrap' }}>
              <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--cor-texto-suave)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Pagamentos
              </div>
              {podeFazer('editar_valores') && podeCobrar && (
                <Link
                  to={`/admin/clientes/${ag.cliente_id}/cobrar?agendamento=${ag.id}`}
                  style={{ fontSize: '13px', textDecoration: 'none', background: 'var(--cor-principal)', color: '#fff', borderRadius: 'var(--raio)', padding: '7px 14px', whiteSpace: 'nowrap' }}
                >
                  🧾 Cobrar
                </Link>
              )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
              <ValorBox titulo="Total" valor={fmtEur(v.total)} />
              <ValorBox titulo="Sinal" valor={fmtEur(v.sinal)} sub={estadoParcelaTexto(est.sinal)} />
              <ValorBox titulo="Restante" valor={fmtEur(v.restante)} sub={estadoParcelaTexto(est.restante, { futuro: new Date(ag.data_hora_inicio) > new Date() })} />
            </div>

            <div style={{ fontSize: '13px', marginTop: '12px', paddingTop: '12px', borderTop: '1px solid var(--cor-borda)' }}>
              Recebido <strong>{fmtEur(v.recebido)}</strong>
              <span style={{ color: 'var(--cor-texto-suave)' }}> · por receber </span><strong>{fmtEur(v.porReceber)}</strong>
            </div>

            {/* Emitir fatura — qualquer parcela paga e ainda por faturar,
                incluindo o sinal pago por Stripe: é dinheiro recebido e
                precisa de documento como qualquer outro. Leva ao modo
                unificado (Cobrar ?modo=fatura), onde a seleção é editável. */}
            {vendusAtivo && podeFazer('editar_valores') && ag.cliente_id &&
             parcelasPorFaturar({ sinal: est.sinal, restante: est.restante }).length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px', marginTop: '12px', paddingTop: '12px', borderTop: '1px solid var(--cor-borda)' }}>
                <div style={{ fontSize: '13px', color: 'var(--cor-texto-suave)' }}>
                  Há pagamentos por faturar
                </div>
                <Link
                  to={`/admin/clientes/${ag.cliente_id}/cobrar?agendamento=${ag.id}&modo=fatura`}
                  style={{ fontSize: '13px', textDecoration: 'none', background: 'var(--cor-principal)', color: '#fff', borderRadius: 'var(--raio)', padding: '7px 14px', whiteSpace: 'nowrap' }}
                >
                  Emitir fatura
                </Link>
              </div>
            )}

            {/* Faturas emitidas (por parcela) + cancelar */}
            {(est.sinal.faturado || est.restante.faturado) && (
              <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid var(--cor-borda)' }}>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                  {faturasEmitidas(est).map(f => (
                    <BotaoObterFatura
                      key={f.faturaId}
                      label={f.label}
                      aObter={obtendoPdf === f.faturaId}
                      onClick={() => obterFatura(f.faturaId)}
                    />
                  ))}
                  {podeFazer('cancelar_fatura') && !confirmarCancelarFatura && (
                    <button
                      onClick={() => setConfirmarCancelarFatura(true)}
                      style={{ ...botaoDoc, marginLeft: 'auto', color: '#DC2626', borderColor: '#FCA5A5' }}
                    >
                      Cancelar fatura
                    </button>
                  )}
                </div>

                {podeFazer('cancelar_fatura') && confirmarCancelarFatura && (
                  <div style={{ marginTop: '10px', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 'var(--raio)', padding: '10px 12px' }}>
                    <p style={{ fontSize: '12.5px', color: '#991B1B', margin: '0 0 8px', lineHeight: 1.5 }}>
                      Cancela a fatura <strong>no sistema</strong>.
                      O documento <strong>não é anulado no Vendus</strong> — tens de o anular lá manualmente. Fica registado nas observações.
                    </p>
                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: '7px', fontSize: '12.5px', color: '#991B1B', cursor: 'pointer', marginBottom: '9px', lineHeight: 1.5 }}>
                      <input
                        type="checkbox"
                        checked={reabrirPagamentos}
                        onChange={e => setReabrirPagamentos(e.target.checked)}
                        style={{ width: '14px', height: '14px', marginTop: '2px', flex: 'none', accentColor: '#DC2626' }}
                      />
                      <span>
                        Marcar como <strong>não paga</strong> — usa isto só se a cobrança em si foi um engano, não apenas o documento.
                        Um pagamento por <strong>Stripe mantém-se pago</strong> de qualquer forma: já foi cobrado ao cliente, fora do nosso controlo.
                      </span>
                    </label>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <button onClick={cancelarFatura} disabled={cancelandoFatura} style={{ background: '#DC2626', color: '#fff', border: 'none', borderRadius: 'var(--raio)', padding: '6px 14px', fontSize: '12.5px', cursor: cancelandoFatura ? 'default' : 'pointer', opacity: cancelandoFatura ? 0.6 : 1 }}>
                        {cancelandoFatura ? 'A cancelar…' : (reabrirPagamentos ? 'Confirmar e reabrir' : 'Confirmar cancelamento')}
                      </button>
                      <button onClick={() => { setConfirmarCancelarFatura(false); setReabrirPagamentos(false) }} disabled={cancelandoFatura} style={{ ...botaoDoc }}>
                        Voltar
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )
      })()}


      {/* Mudar estado */}
      {podeFazer('alterar_estado') && (
      <div style={{ background: 'var(--cor-branco)', border: '1px solid var(--cor-borda)', borderRadius: 'var(--raio-lg)', boxShadow: 'var(--sombra)', padding: '18px 20px', marginBottom: '16px' }}>
        <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--cor-texto-suave)', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Estado da marcação
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '8px' }}>
          {ORDEM_ESTADOS.map(e => {
            const cfg = ESTADOS[e]
            const ativo = ag.estado === e
            return (
              <button
                key={e}
                onClick={() => mudarEstado(e)}
                disabled={guardando}
                style={{
                  padding: '11px 12px', borderRadius: 'var(--raio)', fontSize: '14px', fontWeight: '600',
                  cursor: guardando ? 'default' : 'pointer',
                  border: `1.5px solid ${ativo ? cfg.cor : 'var(--cor-borda)'}`,
                  background: ativo ? cfg.cor : 'var(--cor-branco)',
                  color: ativo ? '#fff' : cfg.cor,
                  opacity: guardando && !ativo ? 0.5 : 1,
                  transition: 'all 0.15s',
                }}
              >
                {cfg.label}
              </button>
            )
          })}
        </div>
      </div>
      )}

      {/* Auditoria */}
      {(ag.atualizado_por || ag.atualizado_em) && (
        <div style={{ fontSize: '12px', color: 'var(--cor-texto-suave)', textAlign: 'center', padding: '8px 0' }}>
          Última alteração
          {ag.atualizado_por && <> por <strong>{ag.atualizado_por}</strong></>}
          {ag.atualizado_em && <> a {format(new Date(ag.atualizado_em), "d MMM yyyy 'às' HH:mm", { locale: pt })}</>}
        </div>
      )}

      {/* Notificações enviadas */}
      <SecaoNotificacoes notificacoes={notificacoes} />
      </div>
      </div>

      {/* Zona de perigo */}
      {podeFazer('eliminar_agendamento') && (
      <div style={{ marginTop: '32px', paddingTop: '24px', borderTop: '1px solid var(--cor-borda)' }}>
        {!confirmarEliminar ? (
          <button
            onClick={() => setConfirmarEliminar(true)}
            style={{ background: 'none', border: '1px solid #FCA5A5', borderRadius: 'var(--raio)', padding: '8px 16px', fontSize: '13px', color: '#DC2626', cursor: 'pointer' }}
          >
            Eliminar marcação
          </button>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '13px', color: '#DC2626', fontWeight: '500' }}>Tens a certeza? Esta ação é irreversível.</span>
            <button
              onClick={eliminar}
              disabled={eliminando}
              style={{ background: '#DC2626', border: 'none', borderRadius: 'var(--raio)', padding: '8px 16px', fontSize: '13px', color: 'white', cursor: eliminando ? 'default' : 'pointer', opacity: eliminando ? 0.6 : 1 }}
            >
              {eliminando ? 'A eliminar…' : 'Confirmar eliminação'}
            </button>
            <button
              onClick={() => setConfirmarEliminar(false)}
              disabled={eliminando}
              style={{ background: 'none', border: '1px solid var(--cor-borda)', borderRadius: 'var(--raio)', padding: '8px 16px', fontSize: '13px', color: 'var(--cor-texto-suave)', cursor: 'pointer' }}
            >
              Cancelar
            </button>
          </div>
        )}
      </div>
      )}
    </div>
  )
}
